<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useField } from 'vee-validate'
import { computed } from 'vue'

import GERMAN_STATES_OPTIONS from '@/constants/germanStateOptions.ts'
import FormSelect from '@/inputs/FormSelect.vue'
import InputGrid from '@/inputs/InputGrid.vue'
import TextInput from '@/inputs/TextInput.vue'
import { useBackendConstantsStore } from '@/stores/backendConstantsStore.ts'
import { translationFunctionDefault } from '@/utilities/translations.ts'

import CheckBox from '../common/ui/inputs/CheckBox.vue'

const { withPrimary = false } = defineProps<{ withPrimary?: boolean }>()

const backendConstantsStore = useBackendConstantsStore()
const { countryOptionList } = storeToRefs(backendConstantsStore)

backendConstantsStore.fetchCountryList().catch((catchError: unknown) => {
  console.error('Failed to fetch country list:', catchError)
})

const tfDefault = translationFunctionDefault()

const { value: countryValue } = useField<string>('country')
const { value: stateValue } = useField<string>('state')
const { value: isPrimaryAddress } = useField<number>('is_primary_address')

const isGermany = (country?: string): boolean => {
  if (!country) return false
  // eslint-disable-next-line id-length
  const c = country.toLowerCase()
  return c === 'germany' || c === 'deutschland'
}

const isInGermanStateOptions = (state: string): boolean => {
  return GERMAN_STATES_OPTIONS.includes({ optionLabel: state, optionValue: state })
}

const showGermanStateDropdown = computed(() => {
  const country = countryValue.value
  const state = stateValue.value

  return isGermany(country) && ((state && isInGermanStateOptions(state)) || !state)
})

const changeIsPrimaryAddress = (): void => {
  isPrimaryAddress.value = isPrimaryAddress.value === 1 ? 0 : 1
}
</script>

<template>
  <InputGrid :columns="2">
    <TextInput
      class="col-span-2"
      :label="tfDefault('editRelationGroup.addressTitlePrimary')"
      name="address_title"
      on-white-bg
      required
    />
    <TextInput
      class="col-span-2"
      :label="tfDefault('editRelationGroup.addressStreetAndNumber')"
      name="address_line1"
      on-white-bg
      required
    />
    <TextInput
      class="col-span-2"
      :label="tfDefault('editRelationGroup.addressMailBox')"
      name="address_line2"
      on-white-bg
    />
    <TextInput
      :label="tfDefault('editRelationGroup.addressZipCode')"
      name="pincode"
      on-white-bg
      required
    />
    <TextInput
      :label="tfDefault('editRelationGroup.addressCity')"
      name="city"
      on-white-bg
      required
    />
    <FormSelect
      v-if="showGermanStateDropdown"
      :label="tfDefault('editRelationGroup.addressFederalState')"
      name="state"
      on-white-bg
      :options="[GERMAN_STATES_OPTIONS]"
      searchable
    />
    <TextInput
      v-else
      :label="tfDefault('editRelationGroup.addressFederalState')"
      name="state"
      on-white-bg
    />
    <FormSelect
      :label="tfDefault('editRelationGroup.addressCountry')"
      name="country"
      on-white-bg
      :options="[countryOptionList]"
      searchable
    />
  </InputGrid>
  <CheckBox
    v-if="withPrimary"
    id="is_primary_address"
    :checked="isPrimaryAddress === 1"
    class="mt-3"
    :label-text="tfDefault('editRelationGroup.addressIsPrimary')"
    on-white-bg
    @change="changeIsPrimaryAddress"
  />
</template>
